import { useEffect, useRef, useState } from "react";
import { Share2, Heart, User } from "lucide-react";
import { trackEvent } from "@/hooks/useEventTracking";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { ShareNeedsModal } from "./ShareNeedsModal";
import { ShareProfileModal } from "./ShareProfileModal";
import type { Shelter } from "@/types/shelter";

interface ShareChoiceDialogProps {
  shelter: Shelter;
  variant?: "button" | "icon";
}

export const ShareChoiceDialog = ({ shelter, variant = "icon" }: ShareChoiceDialogProps) => {
  const [isChoiceOpen, setIsChoiceOpen] = useState(false);
  const [shareType, setShareType] = useState<"needs" | "profile" | null>(null);
  const dialogContentRef = useRef<HTMLDivElement>(null);
  const [isTouchUi, setIsTouchUi] = useState(false);

  useEffect(() => {
    // Na urządzeniach dotykowych iOS Safari potrafi zostawić „sticky focus/hover”,
    // co wygląda jak zaznaczenie pierwszej opcji.
    setIsTouchUi(window.matchMedia?.("(hover: none)")?.matches ?? false);
  }, []);

  useEffect(() => {
    if (!isChoiceOpen) return;
    // iOS/Safari potrafi ustawić focus na pierwszym przycisku w dialogu,
    // co wygląda jak "zaznaczenie". Przenosimy focus na kontener dialogu.
    requestAnimationFrame(() => {
      dialogContentRef.current?.focus();
    });
  }, [isChoiceOpen]);

  const handleIconClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    trackEvent("Udostępnij - otwarto", { shelter_id: shelter.id, shelter_name: shelter.name });
    setIsChoiceOpen(true);
  };

  const handleChooseNeeds = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    trackEvent("Udostępnij potrzeby", { shelter_id: shelter.id, shelter_name: shelter.name });
    setIsChoiceOpen(false);
    setShareType("needs");
  };

  const handleChooseProfile = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    trackEvent("Udostępnij profil", { shelter_id: shelter.id, shelter_name: shelter.name });
    setIsChoiceOpen(false);
    setShareType("profile");
  };

  return (
    <>
      {variant === "icon" ? (
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                onClick={handleIconClick}
                className="p-1.5 rounded-full transition-colors text-muted-foreground hover:text-primary hover:bg-primary/10 [-webkit-tap-highlight-color:transparent]"
                aria-label="Udostępnij"
              >
                <Share2 className="w-4 h-4" />
              </button>
            </TooltipTrigger>
            <TooltipContent>
              <p>Udostępnij</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      ) : (
        <Button onClick={() => { trackEvent("Udostępnij - otwarto", { shelter_id: shelter.id, shelter_name: shelter.name }); setIsChoiceOpen(true); }} className="gap-2 w-full h-auto px-3 py-2 text-sm font-semibold rounded-lg bg-primary text-primary-foreground hover:bg-primary/90">
          <Share2 className="w-4 h-4" />
          Udostępnij
        </Button>
      )}

      {/* Choice Dialog */}
      <Dialog open={isChoiceOpen} onOpenChange={setIsChoiceOpen}>
        <DialogContent
          ref={dialogContentRef}
          tabIndex={-1}
          className="max-w-sm outline-none"
          onOpenAutoFocus={(e) => {
            e.preventDefault();
            dialogContentRef.current?.focus();
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Share2 className="w-5 h-5 text-primary" />
              Co chcesz udostępnić?
            </DialogTitle>
            <DialogDescription>
              Wybierz rodzaj grafiki do udostępnienia.
            </DialogDescription>
          </DialogHeader>

          <div className="grid gap-3 py-4">
            <Button
              variant="outline"
              className="h-auto py-4 flex flex-col items-center gap-2 focus:outline-none focus:ring-0 focus-visible:ring-0 focus-visible:ring-offset-0 hover:bg-background hover:text-foreground sm:hover:bg-accent sm:hover:text-accent-foreground active:bg-background [-webkit-tap-highlight-color:transparent]"
              onFocus={(e) => {
                if (isTouchUi) e.currentTarget.blur();
              }}
              onClick={handleChooseNeeds}
            >
              <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                <Heart className="w-6 h-6 text-primary" />
              </div>
              <div className="text-center">
                <p className="font-semibold">Aktualne potrzeby</p>
                <p className="text-xs text-muted-foreground">
                  Lista rzeczy, których schronisko potrzebuje
                </p>
              </div>
            </Button>

            <Button
              variant="outline"
              className="h-auto py-4 flex flex-col items-center gap-2 focus:outline-none focus:ring-0 focus-visible:ring-0 focus-visible:ring-offset-0 hover:bg-background hover:text-foreground sm:hover:bg-accent sm:hover:text-accent-foreground active:bg-background [-webkit-tap-highlight-color:transparent]"
              onFocus={(e) => {
                if (isTouchUi) e.currentTarget.blur();
              }}
              onClick={handleChooseProfile}
            >
              <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                <User className="w-6 h-6 text-primary" />
              </div>
              <div className="text-center">
                <p className="font-semibold">Profil schroniska</p>
                <p className="text-xs text-muted-foreground">
                  Informacje kontaktowe, wolontariat, opis
                </p>
              </div>
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Needs Modal - controlled mode */}
      <ShareNeedsModal
        shelter={shelter}
        controlledOpen={shareType === "needs"}
        onControlledOpenChange={(open) => !open && setShareType(null)}
      />

      {/* Profile Modal */}
      <ShareProfileModal
        shelter={shelter}
        isOpen={shareType === "profile"}
        onOpenChange={(open) => !open && setShareType(null)}
      />
    </>
  );
};
